import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, Card, CardContent, Chip, CircularProgress,
  Divider, Grid, IconButton, Stack, TableCell, TableRow, TextField,
  Typography, useMediaQuery, useTheme
} from '@mui/material';
import { Add, Delete as DeleteIcon, Edit as EditIcon, Refresh } from '@mui/icons-material';
import { reportCardAPI, academicAPI, studentAPI } from '../../services/api';
import { useAccessControl } from '../../hooks/useAccessControl';
import { RESOURCES, ACTIONS } from '../../hooks/usePermissions';
import ModulePageHeader from '../common/ModulePageHeader';
import ResponsiveRecordList, { EmptyRecordsState } from '../common/ResponsiveRecordList';
import TablePagination from '../common/TablePagination';
import { DataTable, TD_SX, useAppSnackbar, useConfirmDialog } from '../common/TableDesign';
import SubjectCommentFormDialog from './forms/SubjectCommentFormDialog';

const extractCollection = (r) => {
  const p = r?.data !== undefined ? r.data : r;
  if (Array.isArray(p)) return p;
  if (Array.isArray(p?.data)) return p.data;
  if (Array.isArray(p?.data?.data)) return p.data.data;
  return [];
};
const err      = (e, fb = 'Operation failed') => e?.response?.data?.message || e?.response?.data?.error || fb;
const termLabel = (t) => t ? `${t.name} ${t.year || ''}`.trim() : '';
const subjName  = (s) => s?.subject_name || s?.name || '';
const studName  = (s) => s?.full_name || `${s?.last_name || ''} ${s?.first_name || ''}`.trim() || s?.registration_no || '';
const commentText = (c) => c?.comment_text || c?.comment || '';
const acYear    = (t) => Number(t?.academic_year || t?.year || new Date().getFullYear());

const INITIAL_FORM = { student: null, term: null, subject: null, comment_text: '' };
const COLUMNS      = ['Student', 'Class', 'Stream', 'Term', 'Subject', 'Comment', 'Actions'];
const PAGE_SIZE    = 15;

export default function SubjectComments() {
  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { can } = useAccessControl();
  const canRead   = can(RESOURCES.REPORT_CARD.SUBJECT_COMMENTS, ACTIONS.READ);
  const canCreate = can(RESOURCES.REPORT_CARD.SUBJECT_COMMENTS, ACTIONS.CREATE);
  const canUpdate = can(RESOURCES.REPORT_CARD.SUBJECT_COMMENTS, ACTIONS.UPDATE);
  const canDelete = can(RESOURCES.REPORT_CARD.SUBJECT_COMMENTS, ACTIONS.DELETE);
  const { showSnackbar, SnackbarComponent } = useAppSnackbar();
  const { confirm, ConfirmDialogComponent } = useConfirmDialog();

  // data
  const [comments,     setComments]     = useState([]);
  const [terms,        setTerms]        = useState([]);
  const [allSubjects,  setAllSubjects]  = useState([]);
  const [subjects,     setSubjects]     = useState([]);
  const [students,     setStudents]     = useState([]);
  const [classStreams, setClassStreams]  = useState([]);

  // loading / dialog
  const [loadingSetup,    setLoadingSetup]    = useState(true);
  const [loadingComments, setLoadingComments] = useState(false);
  const [saving,          setSaving]          = useState(false);
  const [dialog,          setDialog]          = useState(false);
  const [editingRecord,   setEditingRecord]   = useState(null);
  const [form,            setForm]            = useState(INITIAL_FORM);

  // filters
  const [termFilter,    setTermFilter]    = useState(null);
  const [classFilter,   setClassFilter]   = useState(null);   // { id, name }
  const [streamFilter,  setStreamFilter]  = useState(null);   // { id, name }
  const [studentFilter, setStudentFilter] = useState(null);
  const [subjectFilter, setSubjectFilter] = useState(null);
  const [search,        setSearch]        = useState('');
  const [page,          setPage]          = useState(1);
  const [pageSize,      setPageSize]      = useState(PAGE_SIZE);

  // ── fetch ──────────────────────────────────────────────────────────────────
  const fetchSetup = useCallback(async () => {
    setLoadingSetup(true);
    try {
      const [termRes, subjectRes, studentRes, csRes] = await Promise.all([
        academicAPI.getTerms(),
        reportCardAPI.getActiveSubjects(),
        studentAPI.getActive({ per_page: 2000 }),
        academicAPI.getClassStreamsForFilter(),
      ]);
      const fetchedTerms = extractCollection(termRes);
      setTerms(fetchedTerms);
      setAllSubjects(extractCollection(subjectRes));
      setSubjects(extractCollection(subjectRes));
      setStudents(extractCollection(studentRes));
      setClassStreams(extractCollection(csRes));
      const current = fetchedTerms.find((t) => t.is_current) || null;
      if (current) setTermFilter(current);
    } catch (e) {
      showSnackbar(err(e, 'Failed to load setup data'), 'error');
    } finally {
      setLoadingSetup(false);
    }
  }, []); // eslint-disable-line

  const loadComments = useCallback(async () => {
    setLoadingComments(true);
    try {
      const params = { per_page: 1000 };
      if (studentFilter?.id) params.student_id = studentFilter.id;
      if (subjectFilter?.id) params.subject_id  = subjectFilter.id;
      if (termFilter?.id) {
        params.term_id      = termFilter.id;
        params.academic_year = acYear(termFilter);
      }
      setComments(extractCollection(await reportCardAPI.getSubjectComments(params)));
    } catch (e) {
      setComments([]);
      showSnackbar(err(e, 'Failed to load comments'), 'error');
    } finally {
      setLoadingComments(false);
    }
  }, [studentFilter, subjectFilter, termFilter]); // eslint-disable-line

  useEffect(() => { fetchSetup(); },    [fetchSetup]);
  useEffect(() => { loadComments(); },  [loadComments]);

  // when class+stream filter changes, load subjects for that class/stream
  useEffect(() => {
    if (!classFilter) { setSubjects(allSubjects); setSubjectFilter(null); return; }
    const cs = classStreams.find((c) =>
      c.class_id === classFilter.id && (!streamFilter || c.stream_id === streamFilter.id)
    );
    if (!cs) { setSubjects(allSubjects); return; }
    reportCardAPI.getSubjectPapersForClass({ class_id: cs.class_id, stream_id: cs.stream_id })
      .then((res) => {
        const papers = extractCollection(res);
        // extract unique subjects from the papers
        const seen = new Map();
        papers.forEach((p) => {
          const s = p.subject_paper?.subject || p.subject;
          if (s?.id && !seen.has(s.id)) seen.set(s.id, s);
        });
        const classSubjects = Array.from(seen.values());
        setSubjects(classSubjects.length > 0 ? classSubjects : allSubjects);
        setSubjectFilter(null);
      })
      .catch(() => setSubjects(allSubjects));
  }, [classFilter, streamFilter]); // eslint-disable-line

  // ── derived lists ──────────────────────────────────────────────────────────
  // unique classes from classStreams
  const classes = useMemo(() => {
    const seen = new Map();
    classStreams.forEach((cs) => {
      if (cs.class_id && !seen.has(cs.class_id)) {
        const className = cs.name?.split('-')[0]?.trim() || `Class ${cs.class_id}`;
        seen.set(cs.class_id, { id: cs.class_id, name: className });
      }
    });
    return Array.from(seen.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [classStreams]);

  // streams filtered by selected class
  const streams = useMemo(() => {
    const pool = classFilter
      ? classStreams.filter((cs) => cs.class_id === classFilter.id)
      : classStreams;
    const seen = new Map();
    pool.forEach((cs) => {
      if (cs.stream_id && !seen.has(cs.stream_id)) {
        seen.set(cs.stream_id, { id: cs.stream_id, name: cs.stream_name || `Stream ${cs.stream_id}` });
      }
    });
    return Array.from(seen.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [classStreams, classFilter]);

  // classStream IDs matching class + stream filters
  const matchingCsIds = useMemo(() => {
    if (!classFilter && !streamFilter) return null;
    return new Set(
      classStreams
        .filter((cs) =>
          (!classFilter  || cs.class_id  === classFilter.id) &&
          (!streamFilter || cs.stream_id === streamFilter.id)
        )
        .map((cs) => cs.id)
    );
  }, [classStreams, classFilter, streamFilter]);

  // students narrowed by class/stream
  const filteredStudents = useMemo(() => {
    if (!matchingCsIds) return students;
    return students.filter((s) => matchingCsIds.has(s.class_stream_id));
  }, [students, matchingCsIds]);

  // comments filtered client-side by class/stream + search
  const filtered = useMemo(() => {
    let list = comments;
    if (matchingCsIds) {
      list = list.filter((c) => matchingCsIds.has(c.student?.class_stream_id));
    }
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter((c) =>
      studName(c?.student).toLowerCase().includes(q) ||
      termLabel(c?.term).toLowerCase().includes(q)   ||
      subjName(c?.subject).toLowerCase().includes(q) ||
      commentText(c).toLowerCase().includes(q)
    );
  }, [comments, matchingCsIds, search]);

  const total       = filtered.length;
  const lastPage    = Math.max(1, Math.ceil(total / pageSize));
  const currentPage = Math.min(page, lastPage);
  const paged       = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const pagination  = { current_page: currentPage, last_page: lastPage, per_page: pageSize, total };

  // ── helpers ────────────────────────────────────────────────────────────────
  const getStudentClass  = (s) => {
    const cs = classStreams.find((c) => c.id === s?.class_stream_id);
    return cs?.name?.split('-')[0]?.trim() || '';
  };
  const getStudentStream = (s) => {
    const cs = classStreams.find((c) => c.id === s?.class_stream_id);
    return cs?.stream_name || '';
  };

  const resetForm = () => { setForm(INITIAL_FORM); setEditingRecord(null); setDialog(false); };

  const handleAdd = () => {
    setForm({ ...INITIAL_FORM, term: termFilter || null });
    setDialog(true);
  };

  const handleEdit = (c) => {
    setEditingRecord(c);
    setForm({
      student:      c.student  || null,
      term:         c.term     || null,
      subject:      c.subject  || null,
      comment_text: commentText(c),
    });
    setDialog(true);
  };

  const handleDelete = (c) => {
    confirm({
      title: 'Delete Subject Comment',
      message: `Delete comment for "${studName(c.student)}"? This cannot be undone.`,
      onConfirm: async () => {
        try {
          await reportCardAPI.deleteSubjectComment(c.id);
          showSnackbar('Comment deleted');
          loadComments();
        } catch (e) {
          showSnackbar(err(e, 'Failed to delete'), 'error');
        }
      },
    });
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    const text = form.comment_text.trim();
    if (!form.student?.id || !form.term?.id || !form.subject?.id || !text) {
      showSnackbar('Student, term, subject and comment are required', 'error'); return;
    }
    setSaving(true);
    try {
      const payload = {
        student_id:   form.student.id,
        subject_id:   form.subject.id,
        academic_year: acYear(form.term),
        term_id:      form.term.id,
        comment_text: text,
        comment:      text,
      };
      if (editingRecord) {
        await reportCardAPI.updateSubjectComment(editingRecord.id, payload);
        showSnackbar('Comment updated');
      } else {
        await reportCardAPI.createSubjectComment(payload);
        showSnackbar('Comment saved');
      }
      resetForm();
      loadComments();
    } catch (e) {
      showSnackbar(err(e, 'Failed to save comment'), 'error');
    } finally {
      setSaving(false);
    }
  };

  if (!canRead) return <Alert severity="error">You do not have permission to view subject comments.</Alert>;

  return (
    <Box>
      <ModulePageHeader
        title="Subject Comments"
        subtitle="Manage teacher comments per subject per student"
        actions={[
          <Button key="refresh" variant="outlined" startIcon={<Refresh />}
            onClick={() => { fetchSetup(); loadComments(); }}
            disabled={loadingSetup || loadingComments} fullWidth={isMobile}>
            Refresh
          </Button>,
          canCreate && (
            <Button key="add" variant="contained" startIcon={<Add />}
              onClick={handleAdd} fullWidth={isMobile}>
              Add Comment
            </Button>
          ),
        ].filter(Boolean)}
      />

      {/* ── Filters card ───────────────────────────────────────────────────── */}
      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ pb: '16px !important' }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Filters
          </Typography>
          <Grid container spacing={2}>

            {/* Row 1: Term + Class + Stream */}
            <Grid item xs={12} sm={4}>
              <Autocomplete
                options={terms} value={termFilter}
                onChange={(_, v) => { setTermFilter(v); setPage(1); }}
                getOptionLabel={termLabel}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                renderOption={(props, t) => (
                  <Box component="li" {...props} key={t.id}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2">{termLabel(t)}</Typography>
                      {t.is_current && <Chip label="Current" size="small" color="success" />}
                    </Stack>
                  </Box>
                )}
                renderInput={(p) => <TextField {...p} label="Term" size="small" />}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Autocomplete
                options={classes} value={classFilter}
                onChange={(_, v) => {
                  setClassFilter(v);
                  setStreamFilter(null);
                  setStudentFilter(null);
                  setPage(1);
                }}
                getOptionLabel={(c) => c.name}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                renderInput={(p) => <TextField {...p} label="Class" size="small" />}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Autocomplete
                options={streams} value={streamFilter}
                onChange={(_, v) => { setStreamFilter(v); setStudentFilter(null); setPage(1); }}
                getOptionLabel={(s) => s.name}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                disabled={streams.length === 0}
                renderInput={(p) => <TextField {...p} label="Stream" size="small" />}
              />
            </Grid>

            <Grid item xs={12}><Divider /></Grid>

            {/* Row 2: Student + Subject + Search */}
            <Grid item xs={12} sm={4}>
              <Autocomplete
                options={filteredStudents} value={studentFilter}
                onChange={(_, v) => { setStudentFilter(v); setPage(1); }}
                getOptionLabel={studName}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                renderOption={(props, s) => (
                  <Box component="li" {...props} key={s.id}>
                    <Box>
                      <Typography variant="body2">{studName(s)}</Typography>
                      {s.registration_no && (
                        <Typography variant="caption" color="text.secondary">{s.registration_no}</Typography>
                      )}
                    </Box>
                  </Box>
                )}
                renderInput={(p) => <TextField {...p} label="Student" size="small" />}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Autocomplete
                options={subjects} value={subjectFilter}
                onChange={(_, v) => { setSubjectFilter(v); setPage(1); }}
                getOptionLabel={subjName}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                renderInput={(p) => <TextField {...p} label="Subject" size="small" />}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth size="small" label="Search"
                placeholder="Name, subject, comment..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }} />
            </Grid>

          </Grid>
        </CardContent>
      </Card>

      {/* ── Active filter chips ─────────────────────────────────────────────── */}
      {!loadingComments && (
        <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
          <Chip label={`${total} result${total !== 1 ? 's' : ''}`} size="small" />
          {termFilter   && <Chip label={termLabel(termFilter)}  size="small" color="primary"   variant="outlined" onDelete={() => setTermFilter(null)} />}
          {classFilter  && <Chip label={classFilter.name}       size="small" color="secondary" variant="outlined" onDelete={() => { setClassFilter(null); setStreamFilter(null); setStudentFilter(null); }} />}
          {streamFilter && <Chip label={streamFilter.name}      size="small" color="secondary" variant="outlined" onDelete={() => { setStreamFilter(null); setStudentFilter(null); }} />}
          {subjectFilter && <Chip label={subjName(subjectFilter)} size="small" variant="outlined" onDelete={() => setSubjectFilter(null)} />}
          {studentFilter && <Chip label={studName(studentFilter)} size="small" variant="outlined" onDelete={() => setStudentFilter(null)} />}
        </Stack>
      )}

      {/* ── Results card ───────────────────────────────────────────────────── */}
      <Card>
        <CardContent>
          {loadingComments ? (
            <Box py={6} textAlign="center"><CircularProgress size={28} /></Box>
          ) : filtered.length === 0 ? (
            <EmptyRecordsState message="No subject comments found." />
          ) : isMobile ? (
            <>
              <ResponsiveRecordList
                items={paged}
                renderItem={(c) => (
                  <Stack spacing={0.75}>
                    <Typography fontWeight={700} variant="body2">{studName(c?.student)}</Typography>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      {getStudentClass(c?.student)  && <Chip label={getStudentClass(c?.student)}  size="small" variant="outlined" />}
                      {getStudentStream(c?.student) && <Chip label={getStudentStream(c?.student)} size="small" variant="outlined" />}
                      <Chip label={termLabel(c?.term)}  size="small" color="primary"   variant="outlined" />
                      <Chip label={subjName(c?.subject)} size="small" color="secondary" variant="outlined" />
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      {commentText(c)}
                    </Typography>
                    <Box textAlign="right">
                      {canUpdate && <IconButton size="small" color="primary" onClick={() => handleEdit(c)}><EditIcon fontSize="small" /></IconButton>}
                      {canDelete && <IconButton size="small" color="error"   onClick={() => handleDelete(c)}><DeleteIcon fontSize="small" /></IconButton>}
                    </Box>
                  </Stack>
                )}
              />
              <TablePagination pagination={pagination} onPageChange={setPage}
                onPageSizeChange={(s) => { setPageSize(s); setPage(1); }} isMobile />
            </>
          ) : (
            <>
              <DataTable columns={COLUMNS} data={paged} loading={false}
                emptyMessage="No subject comments found." minWidth={960}
                renderRow={(c) => (
                  <TableRow key={c.id} hover>
                    <TableCell sx={TD_SX}>
                      <Typography variant="body2" fontWeight={600}>{studName(c?.student)}</Typography>
                      {c?.student?.registration_no && (
                        <Typography variant="caption" color="text.secondary">{c.student.registration_no}</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={TD_SX}>
                      <Typography variant="body2">{getStudentClass(c?.student)}</Typography>
                    </TableCell>
                    <TableCell sx={TD_SX}>
                      <Typography variant="body2">{getStudentStream(c?.student)}</Typography>
                    </TableCell>
                    <TableCell sx={TD_SX}>
                      <Chip label={termLabel(c?.term)} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell sx={TD_SX}>
                      <Chip label={subjName(c?.subject)} size="small" color="primary" variant="outlined" />
                    </TableCell>
                    <TableCell sx={{ ...TD_SX, maxWidth: 320 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        {commentText(c)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={TD_SX} align="right">
                      {canUpdate && <IconButton size="small" color="primary" onClick={() => handleEdit(c)}><EditIcon fontSize="small" /></IconButton>}
                      {canDelete && <IconButton size="small" color="error"   onClick={() => handleDelete(c)}><DeleteIcon fontSize="small" /></IconButton>}
                    </TableCell>
                  </TableRow>
                )}
              />
              <TablePagination pagination={pagination} onPageChange={setPage}
                onPageSizeChange={(s) => { setPageSize(s); setPage(1); }} isMobile={isMobile} />
            </>
          )}
        </CardContent>
      </Card>

      <SubjectCommentFormDialog
        open={dialog} onClose={resetForm} onSubmit={handleSubmit}
        editingRecord={editingRecord} formData={form} setFormData={setForm}
        saving={saving} isMobile={isMobile}
        students={students} terms={terms} subjects={subjects}
        classStreams={classStreams}
      />
      {ConfirmDialogComponent}
      {SnackbarComponent}
    </Box>
  );
}
